// react
import { useState } from 'react';
import styled from 'styled-components';

// components
import { Button, ButtonVariant } from '@/components/atoms';
import { FieldDef } from '@/components/organisms';
import { EditorFields } from '@/components/organisms/EditorFields/EditorFields';
import { Icons } from '@/components/atoms/Icon';

interface EditorRevealProps<T = any> {
  className?: string;
  revealLabel: string;
  submitLabel: string;
  revealVariant: ButtonVariant;
  submitVariant: ButtonVariant;
  revealIconLeft?: Icons;
  fields: { [key in keyof T]: FieldDef };
  onSubmit: (value: any) => void;
  isDisabled?: boolean;
}

/**
 * EditorReveal is a component that reveals the editor.
 */
export const EditorReveal = ({
  fields,
  onSubmit,
  revealLabel = 'Reveal',
  submitLabel = 'Submit',
  revealVariant = 'primary',
  submitVariant = 'primary',
  revealIconLeft,
  isDisabled,
}: EditorRevealProps) => {
  // state
  const [value, setValue] = useState<any>({});
  const [isEditor, setIsEditor] = useState(false);
  const [isValid, setIsValid] = useState(false);

  return (
    <Wrapper>
      {isEditor ? (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(value);
          }}
        >
          <EditorFields
            fields={fields}
            value={value}
            onChange={setValue}
            setIsValid={setIsValid}
          />
          <Button
            ariaLabel={submitLabel}
            type='submit'
            variant={submitVariant}
            isFullWidth
            isDisabled={!isValid || isDisabled}
          >
            {submitLabel}
          </Button>
        </Form>
      ) : (
        <Button
          ariaLabel={revealLabel}
          onPress={() => setIsEditor(true)}
          variant={revealVariant}
          iconLeft={revealIconLeft}
          isFullWidth
        >
          {revealLabel}
        </Button>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

EditorReveal.displayName = 'EditorReveal';

export default EditorReveal;
